import React, { FunctionComponent, ReactNode, Ref, useState } from 'react';
import { useTheme } from 'styled-components';

import { BUTTON_TYPES } from '@savgroup-front-common/constants/src/shared';
import { MessageType } from '@savgroup-front-common/types';

import { safeFormattedIntlString } from '../../../../formatters';
import { CrossIcon, LoaderIcon, SearchIcon } from '../../../../protons/icons';

import {
  $SearchActionContainer,
  $SearchClearButton,
  $SearchIconContainer,
  $SearchInput,
  $SearchInputContainer,
  $SearchLoadingContainer,
} from './DataTableSearchInput.styles';
import { SearchInputVariant } from './DataTableSearchInput.types';
import messages from './messages';

interface SearchInputProps {
  value?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onClear?: React.MouseEventHandler<HTMLButtonElement>;
  placeholder?: MessageType | string;
  isLoading?: boolean;
  dataTestId?: string;
  children?: ReactNode;
  variant?: SearchInputVariant;
  searchInputDefaultValue?: string;
  componentThemeName?: string;
}
interface SearchInputWithRefProps extends SearchInputProps {
  forwardedRef: Ref<HTMLInputElement>;
}

const DataTableSearchInput: FunctionComponent<
  React.PropsWithChildren<SearchInputWithRefProps>
> = ({
  value,
  onChange,
  onBlur,
  onFocus,
  isLoading = false,
  forwardedRef,
  onKeyDown,
  onClear,
  dataTestId = 'searchInput',
  placeholder = messages.placeholder,
  children,
  variant = SearchInputVariant.DEFAULT,
  searchInputDefaultValue,
  componentThemeName,
}) => {
  const [valueSearched, setValueSearched] = useState<string>();
  const theme = useTheme();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValueSearched(e.target.value);

    if (onChange) {
      onChange(e);
    }
  };

  return (
    <$SearchInputContainer
      $componentThemeName={componentThemeName}
      $variant={variant}
    >
      <$SearchIconContainer>
        <SearchIcon color={theme.colors.mainTextColor} size="24px" />
        {isLoading && (
          <$SearchLoadingContainer>
            <LoaderIcon
              color={theme.colors.mainTextColor}
              size="15px"
              strokeWidth="10"
            />
          </$SearchLoadingContainer>
        )}
      </$SearchIconContainer>

      <$SearchInput
        defaultValue={searchInputDefaultValue}
        $variant={variant}
        placeholder={safeFormattedIntlString(placeholder)}
        onChange={handleChange}
        onBlur={onBlur}
        value={value || valueSearched}
        onFocus={onFocus}
        ref={forwardedRef}
        onKeyDown={onKeyDown}
        data-testid={`${dataTestId}_input`}
      />
      <$SearchActionContainer>
        {onClear && (
          <$SearchClearButton
            icon={<CrossIcon />}
            naked
            type={BUTTON_TYPES.BUTTON}
            onClick={onClear}
            dataTestId={`${dataTestId}_clearButton`}
          />
        )}
        {children}
      </$SearchActionContainer>
    </$SearchInputContainer>
  );
};

export default React.forwardRef<HTMLInputElement, SearchInputProps>(
  (props, ref) => <DataTableSearchInput forwardedRef={ref} {...props} />,
);
